import { ErrorToast, SuccessToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { setIsDealsheetReload, setIsNotesOpen } from '@store/dealsheet/DealsheetActions';
import { DealsheetState } from '@store/dealsheet/DealsheetReducer';
import { theme } from '@theme';
import { debounce } from 'lodash';
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchDeals } from '../../../hooks/header/UseSearchDeals';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import { useDealType } from '../../../hooks/UseDealType';
import { useOutsideClickDetection } from '../../../hooks/UseOutsideClickDetection';
import { DealsService } from '../../../services/shared/DealsService';
import { SearchResult } from '../../../types/header/SearchResult';
import { DisplayContractModal } from '../../shared/essential-ui/modals/contract-modal/DisplayContractModal';
import { Icon } from '../../style/Icon';
import ReactivateDealModal from './reactivate-deal/ReactivateDealModal';
import SearchResults from './search-results/SearchResults';
import SearchWrapper from './search-wrapper/SearchWrapper';
import {
  SearchDealsDiv,
  SearchDealsInnerWrapper,
  SearchDealsInput,
  SearchForm,
} from './SearchDealsStyles';

type SearchDealsProps = {
  showInactiveDeals: boolean;
  isIncentiveAdvantageType: boolean;
};

const SearchDeals: FC<SearchDealsProps> = ({ showInactiveDeals, isIncentiveAdvantageType }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchResultsVisible, setIsSearchResultsVisible] = useState<boolean>(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState<boolean>(false);
  const [isNoResultsVisible, setIsNoResultsVisible] = useState<boolean>(false);
  const [isLoadingIndicatorVisible, setIsLoadingIndicatorVisible] = useState<boolean>(false);
  const [reactivateDeal, setReactivateDeal] = useState<SearchResult>();
  const [shouldOpenModal, setShouldOpenModal] = useState<boolean>(false);
  const { getDealTypeLabel } = useDealType();

  const { searchHistory, saveSearchToHistory, clearHistory } = useSearchDeals();
  const [isSearchVisibleForMobile, setIsSearchVisibleForMobile] = useState<boolean>(false);
  const { isMobileDevice, isTabletDevice } = useCheckDevice();
  const [outsideClicked, setOutsideClicked] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDealsheetReload } = useSelector<AppState, DealsheetState>(
    (state) => state.dealsheetModernized,
  );
  const handleOutSideClick = (): void => {
    setOutsideClicked(true);
    hideSearchDialogs();
  };

  useEffect(() => {
    if (outsideClicked) {
      setOutsideClicked(false);
      isMobileDevice && setIsSearchVisibleForMobile(false);
    }
  }, [outsideClicked]);

  useOutsideClickDetection(wrapperRef, handleOutSideClick);

  const clearSearch = () => {
    inputRef.current.value = '';
    setSearchTerm('');
    setSearchResults([]);
    hideSearchDialogs();
    setIsSearchVisibleForMobile(false);
  };

  const hideSearch = () => {
    clearSearch();
    setIsLoadingIndicatorVisible(false);
  };

  const goToDealsheet = (item: SearchResult): void => {
    if (!item.isActive && isMobileDevice) {
      return;
    }

    if (!item.isActive) {
      // open the reactivation modal instead
      setReactivateDeal(item);
      setShouldOpenModal(true);
      hideSearchDialogs();
      return;
    }

    // delete the search term
    setSearchTerm('');
    inputRef.current.value = '';

    hideSearchDialogs();
    setShouldOpenModal(false);
    setIsSearchVisibleForMobile(false);

    if (isIncentiveAdvantageType) {
      navigate(`/essential/dealsheet/${item.closedDealId}/${item.dealerId}`);
    } else {
      navigate(
        `/dealsheet/${item.vin}/${item.dealerId}/offer/1/${getDealTypeLabel(item.dealType).toLowerCase()}/bps`,
      );
      dispatch(setIsDealsheetReload(!isDealsheetReload));
      dispatch(setIsNotesOpen(false));
    }

    // Set up previous location for the back button to redirect user back to it
    // If user goes to loyalty deal sheet via notification from turn deal sheet
    // Turn deal sheet is set to be their last location for them to return to
    const currentURL = window.location.pathname;
    const urlParts = currentURL.split('/');
    const turnPath = urlParts[1] + '/' + urlParts[2];
    const lastNavigation = JSON.parse(localStorage.getItem('lastNavigation') ?? '');
    if (lastNavigation.state === 'app.turn.details' && turnPath !== 'turn/details') {
      const vin = urlParts[2];
      const dealerId = urlParts[3];
      const offerId = urlParts[5];
      const offerDealType = urlParts[6];
      const turnDealsheetRoute = `/dealsheet/${vin}/${dealerId}/offer/${offerId}/${offerDealType}/bps${location.search}`;
      const turnDealsheetNav = { state: 'app.turn.dealsheet', route: turnDealsheetRoute };
      localStorage.setItem('lastNavigation', JSON.stringify(turnDealsheetNav));
    }
  };

  const onFocusSearchField = () => {
    setHistoryVisible();
    setSearchResultsVisible();
    setNoResultsVisible();
  };

  const setSearchResultsVisible = () => {
    setIsSearchResultsVisible(searchResults.length !== 0 && searchTerm.length > 0);
  };

  const setHistoryVisible = () => {
    setIsHistoryVisible(
      isLoadingIndicatorVisible === false &&
        isSearchResultsVisible === false &&
        searchTerm.length === 0 &&
        searchHistory.length !== 0,
    );
  };

  const setNoResultsVisible = () => {
    setIsNoResultsVisible(
      searchTerm.length >= 3 &&
        searchResults.length === 0 &&
        isLoadingIndicatorVisible === false &&
        isSearchResultsVisible === false,
    );
  };

  const hideSearchDialogs = () => {
    setIsHistoryVisible(false);
    setIsNoResultsVisible(false);
    setIsSearchResultsVisible(false);
  };

  const showSearchWrapper = () => {
    return isHistoryVisible || isLoadingIndicatorVisible || isNoResultsVisible;
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const onClearHistory = (): void => {
    setIsHistoryVisible(false);
    clearHistory();
  };

  const onSearchHistoryClick = (term: string, event: React.MouseEvent<HTMLElement>): void => {
    setSearchTerm(term);
    inputRef.current.value = term;
    event.stopPropagation();
  };

  const onReactivateDeal = (): void => {
    DealsService.reactivateDeal(
      reactivateDeal.dealerId,
      reactivateDeal.vin,
      reactivateDeal.closedDealId,
    )
      .then(() => {
        setSearchTerm('');
        inputRef.current.value = '';
        SuccessToast('Success! The selected deal has been reactivated.');

        if (!isIncentiveAdvantageType) {
          setTimeout(() => {
            navigate(
              `/dealsheet/${reactivateDeal?.vin}/${reactivateDeal?.dealerId}/offer/1/${getDealTypeLabel(reactivateDeal?.dealType).toLowerCase()}/bps`,
            );
          }, 4000);
        } else {
          location.reload();
        }
      })
      .catch(() => {
        ErrorToast('There was a problem trying to reactivate the selected deal. Please try again.');
      })
      .finally(() => {
        hideSearchDialogs();
      });
  };

  const debounceOnInputChange = useCallback(debounce(onInputChange, 500), []);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      if (isMobileDevice) {
        setIsLoadingIndicatorVisible(isSearchVisibleForMobile);
      } else {
        setIsLoadingIndicatorVisible(true);
      }
      saveSearchToHistory(searchTerm);
      DealsService.searchDeals(searchTerm, showInactiveDeals)
        .then((deals: SearchResult[]) => {
          const updatedDeals = deals.map((deal: SearchResult) => {
            if (deal.firstName === null) {
              deal.firstName = '';
            }
            deal.linkToDealsheet = '/dealsheet/' + deal.vin;
            return deal;
          });

          setSearchResults(updatedDeals);
        })
        .catch(() => {
          ErrorToast('An error has occurred while processing your search. Please try again');
        })
        .finally(() => {
          setIsLoadingIndicatorVisible(false);
        });
    }
  }, [searchTerm]);

  useEffect(() => {
    if (
      searchResults.length > 0 &&
      searchTerm.length >= 3 &&
      (isMobileDevice ? isSearchVisibleForMobile : true)
    ) {
      setIsSearchResultsVisible(true);
    } else {
      setIsSearchResultsVisible(false);
    }
    if (
      searchResults.length === 0 &&
      searchTerm.length >= 3 &&
      !isLoadingIndicatorVisible &&
      !isSearchResultsVisible &&
      (isMobileDevice ? isSearchVisibleForMobile : true)
    ) {
      setIsNoResultsVisible(true);
    }
  }, [searchResults, isLoadingIndicatorVisible]);

  useEffect(() => {
    if (isLoadingIndicatorVisible) {
      hideSearchDialogs();
    }
  }, [isLoadingIndicatorVisible]);

  const searchIconClickEvent = () => {
    hideSearchDialogs();
    setIsSearchVisibleForMobile(!isSearchVisibleForMobile);
  };

  useEffect(() => {
    if (isSearchVisibleForMobile) {
      (document.getElementById('search-input') as HTMLInputElement).value = searchTerm;
    }
    setIsSearchResultsVisible(isSearchResultsVisible && isSearchVisibleForMobile);
    setIsHistoryVisible(isSearchResultsVisible && isSearchVisibleForMobile);
    setIsNoResultsVisible(isSearchResultsVisible && isSearchVisibleForMobile);
  }, [isSearchVisibleForMobile]);

  return (
    <>
      <SearchDealsDiv>
        <SearchForm
          isMobileDevice={isMobileDevice}
          isTabletDevice={isTabletDevice}
          ref={wrapperRef}
          data-testid='search-deals'
        >
          {isMobileDevice && (
            <i
              data-testid='search-icon'
              className='am-icon am-icon-loupe'
              onClick={searchIconClickEvent}
            />
          )}

          <SearchDealsInnerWrapper isMobileDevice={isMobileDevice}>
            {(!isMobileDevice || isSearchVisibleForMobile) && (
              <SearchDealsInput isMobileDevice={isMobileDevice}>
                <input
                  className={isMobileDevice ? `search-mobile-input` : ''}
                  type='text'
                  placeholder='Search Deals...'
                  autoComplete='off'
                  autoCorrect='off'
                  autoCapitalize='off'
                  spellCheck={false}
                  onFocus={onFocusSearchField}
                  onKeyUp={setHistoryVisible}
                  onChange={debounceOnInputChange}
                  ref={inputRef}
                  data-testid='search-input'
                  id='search-input'
                />
                {isMobileDevice ? (
                  <Icon
                    data-testid='search-input-cross-icon'
                    type='x'
                    additionalClassName='am-icon'
                    size={theme.typography.h1}
                    onClick={hideSearch}
                  />
                ) : (
                  <>
                    <Icon
                      type='loupe'
                      additionalClassName='am-icon'
                      size={theme.typography.h4}
                      data-testid='loupe-icon'
                    />
                    {!isMobileDevice && searchTerm.length > 0 && (
                      <Icon
                        type='x'
                        additionalClassName='am-icon'
                        size={theme.typography.fontSize21}
                        onClick={clearSearch}
                      />
                    )}
                  </>
                )}
              </SearchDealsInput>
            )}
            {showSearchWrapper() && (isMobileDevice ? isSearchVisibleForMobile : true) && (
              <SearchWrapper
                isHistoryVisible={isHistoryVisible}
                isLoadingIndicatorVisible={isLoadingIndicatorVisible}
                isNoResultsVisible={isNoResultsVisible}
                onClearHistory={onClearHistory}
                onSearchHistoryClick={onSearchHistoryClick}
              />
            )}

            {isSearchResultsVisible && (
              <SearchResults goToDealsheet={goToDealsheet} searchResults={searchResults} />
            )}
          </SearchDealsInnerWrapper>
        </SearchForm>
      </SearchDealsDiv>
      {!isMobileDevice && reactivateDeal && !isIncentiveAdvantageType && (
        <ReactivateDealModal
          searchResult={reactivateDeal}
          shouldOpenModal={shouldOpenModal}
          setShouldOpenModal={setShouldOpenModal}
          onReactivateDeal={onReactivateDeal}
        />
      )}
      {!isMobileDevice && reactivateDeal && isIncentiveAdvantageType && (
        <DisplayContractModal
          chromeId={reactivateDeal?.chromeId.toString()}
          closedDealId={reactivateDeal?.closedDealId}
          dealerId={reactivateDeal?.dealerId}
          shouldOpenModal={shouldOpenModal}
          setShouldOpenModal={setShouldOpenModal}
        />
      )}
    </>
  );
};

export default SearchDeals;
